import { hasScope, type ApiClient } from '@nyce/admin-kit/api';
import { SCOPES } from '../../api';
import { client } from '../../session';

/**
 * Production statistics (`docs/production-stats.md`, `ProductionStats/ProdStatsEndpoints.cs`): what the nyce.logic
 * databases produced per day, counted every night on one copy of each database (a replica when there is one). Days are
 * the WMS's own `dateadded` days; the views end before today, which is still being counted.
 */

export type MetricId = 'transactions' | 'cod' | 'cod_lines' | 'shipment_refs' | 'pd' | 'pd_lines';

export interface Metric {
  id: MetricId;
  label: string;
  /** Split by transaction type code. */
  byType: boolean;
}

export interface MetricTotals {
  id: MetricId;
  label: string;
  yesterday: number;
  last7: number;
  last30: number;
  yearToDate: number;
}

export type RunStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

export interface ProdRun {
  id: number;
  status: RunStatus;
  scheduled: boolean;
  serverIds: string[];
  databases: string[];
  createdBy: string;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  cancelRequested: boolean;
  databasesTotal: number;
  databasesDone: number;
  databasesFailed: number;
  rowsCounted: number;
  error: string | null;
}

export interface ProdTotals {
  /** Today in the schedule's zone (yyyy-MM-dd). */
  today: string;
  firstDay: string | null;
  metrics: MetricTotals[];
  lastRun: ProdRun | null;
  lastFinished: ProdRun | null;
  schedule: { enabled: boolean; nextRunAt: string | null };
}

export interface DailyRow {
  day: string;
  metric: MetricId;
  /** The transaction type's code; '' for the other metrics. */
  typeCode: string;
  count: number;
}

export interface Daily {
  from: string;
  /** Exclusive. */
  to: string;
  metrics: Metric[];
  rows: DailyRow[];
}

export interface DatabaseRow {
  database: string;
  /** The registered environment whose database this is. */
  environment: { id: string; name: string; customer: string } | null;
  counts: Partial<Record<MetricId, number>>;
  lastDay: string | null;
}

export interface RunDatabase {
  database: string;
  /** The copy it was counted on. */
  serverId: string;
  identity: string | null;
  standby: boolean;
  status: 'ok' | 'error' | 'skipped';
  note: string | null;
  rows: number;
  /** The first count of this database (its whole history). */
  backfill: boolean;
}

export interface Schedule {
  cron: string;
  timezone: string;
  enabled: boolean;
  excluded: string[];
  nextRunAt: string | null;
  attempts: number;
  updatedAt: string;
  updatedBy: string;
}

export interface ScheduleInput {
  cron: string;
  timezone: string;
  enabled: boolean;
  excluded: string[];
}

export interface RangeQuery {
  from?: string;
  to?: string;
  metric?: MetricId;
  database?: string;
  includeArchive?: boolean;
}

function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '' && v !== false) q.set(k, String(v));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function prodStatsApi(c: ApiClient) {
  return {
    totals: (includeArchive = false) =>
      c.get<ProdTotals>(`/production-stats/totals${query({ includeArchive })}`),
    daily: (q: RangeQuery) => c.get<Daily>(`/production-stats/daily${query({ ...q })}`),
    databases: (q: Omit<RangeQuery, 'metric' | 'database'>) =>
      c.get<{ from: string; to: string; metrics: Metric[]; databases: DatabaseRow[] }>(
        `/production-stats/databases${query({ ...q })}`
      ),
    runs: (limit = 10) => c.get<ProdRun[]>(`/production-stats/runs${query({ limit })}`),
    run: (id: number) => c.get<{ run: ProdRun; databases: RunDatabase[] }>(`/production-stats/runs/${id}`),
    start: (databases: string[] = []) => c.post<ProdRun>('/production-stats/runs', { databases }),
    cancel: (id: number) => c.post<ProdRun>(`/production-stats/runs/${id}/cancel`),
    schedule: () => c.get<Schedule>('/production-stats/schedule'),
    saveSchedule: (body: ScheduleInput) => c.put<Schedule>('/production-stats/schedule', body),
  };
}

export const prodStats = prodStatsApi(client);

export const PRODUCTION_PATH = '/statistics/production';

/** May start runs and edit the schedule (`ophub:db:advise`, as fleet index scans). */
export const canRun = (scopes: readonly string[] | null | undefined) => hasScope(scopes, SCOPES.dbAdvise);

export const isRunActive = (r: Pick<ProdRun, 'status'> | null | undefined) =>
  r?.status === 'queued' || r?.status === 'running';
